import { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page as PDFPage } from "react-pdf";
import { LucideFilePlus } from "lucide-react";

/**
 * Scrollable PDF preview panel.
 *
 * Renders all pages as canvas tiles and smooth-scrolls to the active field
 * when `scrollTarget` changes - without reloading the PDF on live regen.
 *
 * Props:
 *   src              - object URL of the filled PDF (null = show placeholder)
 *   scrollTarget     - { page: number, topFraction: 0–1 } | null
 *   defaultPageCount - number of skeleton pages to show before the PDF loads
 *                      (default 2; use 4 for four-page templates)
 *
 * Note: pdfjs.GlobalWorkerOptions.workerSrc must be set in the parent file
 * before this component is rendered (it already is in each Page.jsx).
 */
export function PDFPreview({ src, scrollTarget, defaultPageCount = 2 }) {
  const containerRef      = useRef(null);
  const pageRefs          = useRef({});        // { 1: divEl, 2: divEl, … }
  const scrollTimerRef    = useRef(null);
  const scrollTargetRef   = useRef(scrollTarget);
  const isFirstLoad       = useRef(true);
  const savedScrollTopRef = useRef(0);
  const [numPages, setNumPages]             = useState(null);
  const [containerWidth, setContainerWidth] = useState(480);
  const [pdfVisible, setPdfVisible]         = useState(true);

  // Keep a ref to the latest scrollTarget so timeout callbacks are never stale
  useEffect(() => { scrollTargetRef.current = scrollTarget; }, [scrollTarget]);

  // Save scroll position and fade out before each PDF regen
  useEffect(() => {
    if (!isFirstLoad.current && containerRef.current) {
      savedScrollTopRef.current = containerRef.current.scrollTop;
      setPdfVisible(false);
    }
  }, [src]);

  // Measure container width so pages fill the panel width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(Math.floor(entry.contentRect.width) - 8);
    });
    ro.observe(el);
    setContainerWidth(Math.floor(el.getBoundingClientRect().width) - 8);
    return () => ro.disconnect();
  }, []);

  // Smooth-scroll the container to the target position (reads from ref - always fresh)
  const doScroll = useCallback(() => {
    const target = scrollTargetRef.current;
    if (!target || !containerRef.current) return;
    const { page, topFraction } = target;
    const pageEl = pageRefs.current[page];
    if (!pageEl) return;
    const containerEl  = containerRef.current;
    const pageRect     = pageEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    const newScrollTop =
      containerEl.scrollTop +
      (pageRect.top - containerRect.top) +
      topFraction * pageEl.offsetHeight -
      20;
    containerEl.scrollTo({ top: Math.max(0, newScrollTop), behavior: "smooth" });
  }, []); // stable - only reads refs

  // Trigger scroll when the step (scrollTarget) changes - NOT on PDF regen.
  // src is intentionally excluded from deps: live PDF updates should not scroll.
  useEffect(() => {
    if (!src) return;
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(doScroll, 80);
    return () => clearTimeout(scrollTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollTarget, doScroll]);

  return (
    <div
      ref={containerRef}
      className="overflow-y-auto h-full bg-slate-100 flex flex-col items-center py-5 px-4 gap-4"
    >
      {src ? (
        <div
          style={{
            opacity: pdfVisible ? 1 : 0,
            transition: pdfVisible ? "opacity 800ms ease-in" : "none",
            display: "contents",
          }}
        >
        <Document
          file={src}
          onLoadSuccess={({ numPages: n }) => {
            setNumPages(n);
            if (isFirstLoad.current) {
              isFirstLoad.current = false;
              if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
              scrollTimerRef.current = setTimeout(doScroll, 300);
            } else {
              // Restore scroll position then fade in
              if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
              scrollTimerRef.current = setTimeout(() => {
                if (containerRef.current) {
                  containerRef.current.scrollTop = savedScrollTopRef.current;
                }
                setPdfVisible(true);
              }, 50);
            }
          }}
        >
          {Array.from({ length: numPages ?? defaultPageCount }, (_, i) => (
            <div
              key={i + 1}
              ref={(el) => { pageRefs.current[i + 1] = el; }}
              className="shadow-lg rounded overflow-hidden"
            >
              <PDFPage
                pageNumber={i + 1}
                width={containerWidth || 480}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </div>
          ))}
        </Document>
        </div>
      ) : (
        <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
          <LucideFilePlus size={40} strokeWidth={1.5} />
          <p className="text-sm">Click &ldquo;Generate PDF&rdquo; to preview</p>
        </div>
      )}
    </div>
  );
}
